import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Alert,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Stack,
  Typography,
} from '@mui/material';
import { LockRounded, CheckCircleRounded, ArrowBackRounded } from '@mui/icons-material';
import { SurveyQuestionInput } from '../components/SurveyQuestionInput';
import { signOutAndReload } from '../../../lib/session';
import { surveyApi, type SurveyAnswerValue } from '../../../lib/api/survey.api';
import { useTenantStore } from '../../../stores/tenant.store';
import { useStudentIdentity } from '../../../hooks/use-student-identity';
import { errorText } from '../../../lib/error-text';

export function SurveyDetailPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { surveyId } = useParams<{ surveyId: string }>();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { currentTenantId } = useTenantStore();
  const { personId } = useStudentIdentity();

  const { data: result, isLoading } = useQuery({
    queryKey: ['portal-v2-survey', currentTenantId, surveyId, personId],
    enabled: !!currentTenantId && !!surveyId,
    queryFn: () =>
      surveyApi.getForStudent({
        tenantId: currentTenantId!,
        surveyId: surveyId!,
      }),
    staleTime: 30_000,
  });
  const survey = result?.reason === 'ok' ? result.survey : undefined;

  const [answers, setAnswers] = useState<Record<string, SurveyAnswerValue>>({});
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const isAnswered = (v: SurveyAnswerValue | undefined): boolean =>
    v !== undefined && v !== '' && !(Array.isArray(v) && v.length === 0);

  const missingRequired = useMemo(
    () => (survey?.questions ?? []).some((q) => q.required && !isAnswered(answers[q.id])),
    [survey, answers],
  );

  const handleSubmit = async () => {
    if (!survey || !currentTenantId) return;
    setSubmitting(true);
    setError(null);
    try {
      const payload = survey.questions
        .filter((q) => isAnswered(answers[q.id]))
        .map((q) => ({ questionId: q.id, value: answers[q.id]! }));
      await surveyApi.submit({
        tenantId: currentTenantId,
        surveyId: survey.id,
        answers: payload,
      });
      await queryClient.invalidateQueries({ queryKey: ['portal-v2-open-surveys'] });
      await queryClient.invalidateQueries({ queryKey: ['portal-v2-survey'] });
      setDone(true);
    } catch (e) {
      setError(errorText(e, t('portal.surveys.errorGeneric')));
    } finally {
      setSubmitting(false);
    }
  };

  if (isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (done) {
    return (
      <Box sx={{ p: 3, maxWidth: 620, mx: 'auto', textAlign: 'center' }}>
        <CheckCircleRounded sx={{ fontSize: 64, color: 'success.main', mb: 2 }} />
        <Typography variant="h5" fontWeight={700} gutterBottom>
          {t('portal.surveys.thanksTitle')}
        </Typography>
        <Typography color="text.secondary" sx={{ mb: 3 }}>
          {survey?.confirmationMessage || t('portal.surveys.thanksBody')}
        </Typography>
        <Button variant="contained" onClick={() => navigate('/portal-v2/surveys')}>
          {t('portal.surveys.backToList')}
        </Button>
      </Box>
    );
  }

  if (!survey) {
    // Reason-specific message so the student knows WHY (closed, not enrolled,
    // out of window, already answered) instead of a generic "unavailable".
    const reason = result?.reason ?? 'not_found';
    const reasonMsg = t(`portal.surveys.reason.${reason}`, {
      defaultValue: t('portal.surveys.unavailable'),
    });
    // Wrong-account case: let them sign out and return to this exact survey so
    // they can retry with their own student account.
    const switchAccount = async () => {
      // Vuelve a ESTA encuesta a propósito: es la misma persona que entró con
      // la cuenta equivocada. Igual recarga (lib/session).
      const returnUrl = encodeURIComponent(`/portal-v2/surveys/${surveyId}`);
      await signOutAndReload(`/login?returnUrl=${returnUrl}`);
    };
    return (
      <Box sx={{ p: 3, maxWidth: 620, mx: 'auto' }}>
        <Button startIcon={<ArrowBackRounded />} onClick={() => navigate('/portal-v2/surveys')}>
          {t('portal.surveys.backToList')}
        </Button>
        <Alert severity={reason === 'already_answered' ? 'success' : 'info'} sx={{ mt: 2 }}>
          {reasonMsg}
        </Alert>
        {reason === 'no_student_profile' && (
          <Button variant="contained" sx={{ mt: 2 }} onClick={switchAccount}>
            {t('portal.surveys.switchAccount')}
          </Button>
        )}
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 720, mx: 'auto' }}>
      <Button
        startIcon={<ArrowBackRounded />}
        onClick={() => navigate('/portal-v2/surveys')}
        sx={{ mb: 1 }}
      >
        {t('portal.surveys.backToList')}
      </Button>

      <Typography variant="h5" fontWeight={700}>
        {survey.title}
      </Typography>
      <Chip
        size="small"
        icon={<LockRounded />}
        label={t('portal.surveys.anonymousNote')}
        color="success"
        variant="outlined"
        sx={{ mt: 1 }}
      />
      {survey.welcomeMessage && (
        <Typography color="text.secondary" sx={{ mt: 2 }}>
          {survey.welcomeMessage}
        </Typography>
      )}

      <Card variant="outlined" sx={{ borderRadius: 3, mt: 3 }}>
        <CardContent>
          <Stack spacing={3} divider={<Divider flexItem />}>
            {survey.questions.map((q) => (
              <SurveyQuestionInput
                key={q.id}
                question={q}
                value={answers[q.id]}
                onChange={(v) => setAnswers((prev) => ({ ...prev, [q.id]: v }))}
              />
            ))}
          </Stack>
        </CardContent>
      </Card>

      {error && (
        <Alert severity="error" sx={{ mt: 2 }}>
          {error}
        </Alert>
      )}

      <Button
        variant="contained"
        size="large"
        fullWidth
        disabled={missingRequired || submitting}
        onClick={handleSubmit}
        sx={{ mt: 3 }}
      >
        {submitting ? t('portal.surveys.submitting') : t('portal.surveys.submit')}
      </Button>
    </Box>
  );
}
